import { Suspense, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { pageRoutes } from 'shared/routes';
import { useClientDataStore, Roles } from 'shared/lib/store';
import { activateImpersonateSession, isUserCameAsImpersonated } from 'entities/contractor';
import { Loader } from 'shared/ui/Loader';
import { Error404 } from 'pages/Error404';
import { getClientData } from 'shared/lib/api';
import { TermsAndConditionPopup } from 'features/terms-and-conditions';
import { Share } from 'pages/Share';
import { ContractorReports } from 'pages/ContractorReports';
import { CampaignMap, OverviewReport, PerformanceMetricsReport, VerticalBenchmark } from 'widgets/ContractorTSReports';

import { sentryInit } from './userSentry';
import { ContractorLayoutContent } from './Layout/ContractorLayoutContent';
import { AppLayout } from './Layout/AppLayout';
import { setRedirectUri } from './lib';
import {
  ContractorCampaigns,
  ImpersonateError,
  Invoices,
  ManagerAddCampaigns,
  ManagerAppointments,
  ManagerAuth,
  ManagerCampaigns,
  ManagerContractors,
  ManagerCreateNewGroup,
  ManagerCreatePassword,
  ManagerGroupView,
  ManagerGroups,
  ManagerLeads,
  ManagerSales,
  ManagerPasswordRecovery,
  Billing,
  ContractorAppointments,
  ContractorAuth,
  ContractorCreatePassword,
  ContractorLeads,
  ContractorPasswordRecovery,
  MyAccount,
  ContractorSales,
  ManagerOverlapping,
  FaqPage,
} from './lazy-pages';
import { AppLoader } from './AppLoader';

const publicPages = [
  pageRoutes.internal.auth,
  pageRoutes.app.recovery,
  pageRoutes.app.auth,
  pageRoutes.app.newPassword,
  pageRoutes.internal.recovery,
  pageRoutes.internal.newPassword,
];

sentryInit();

export function App() {
  const { setRole, getRole } = useClientDataStore();
  const [isAppLoaded, setIsAppLoaded] = useState(false);
  const role = getRole();

  const history = useHistory();

  useEffect(() => {
    if (!isUserCameAsImpersonated()) {
      setIsAppLoaded(true);

      return;
    }

    activateImpersonateSession()
      .catch(() => {
        history.push(pageRoutes.app.impersonateError);
      })
      .finally(() => {
        setIsAppLoaded(true);
      });
  }, []);

  useEffect(() => {
    const savedRole = getClientData().role;

    setRole(savedRole);

    if (!savedRole && !publicPages.includes(history.location.pathname)) {
      const replaceUrl = setRedirectUri();

      history.replace(replaceUrl);
    }
  }, []);

  if (!isAppLoaded) {
    return <AppLoader />;
  }

  return (
    <AppLayout
      role={role}
      publicPages={publicPages}
    >
      <Suspense fallback={<Loader className="pointer-events-auto mt-20 flex justify-center" />}>
        <Switch>
          <Route
            path={pageRoutes.internal.auth}
            component={ManagerAuth}
            exact
          />
          <Route
            path={pageRoutes.app.auth}
            component={ContractorAuth}
            exact
          />
          <Route
            path={pageRoutes.app.impersonateError}
            component={ImpersonateError}
          />
          <Route
            path={pageRoutes.app.notFound}
            component={Error404}
          />
          <Route
            path={pageRoutes.app.recovery}
            component={ContractorPasswordRecovery}
          />
          <Route
            path={pageRoutes.app.newPassword}
            component={ContractorCreatePassword}
          />
          <Route
            path={pageRoutes.internal.recovery}
            component={ManagerPasswordRecovery}
          />
          <Route
            path={pageRoutes.internal.newPassword}
            component={ManagerCreatePassword}
          />
          {role === Roles.Contractor && (
            <Route>
              <ContractorLayoutContent>
                <Switch>
                  <Route path={pageRoutes.app.campaigns}>
                    <ContractorCampaigns />
                  </Route>
                  <Route path={pageRoutes.app.leads}>
                    <ContractorLeads />
                  </Route>
                  <Route path={pageRoutes.app.appointments}>
                    <ContractorAppointments />
                  </Route>
                  <Route path={pageRoutes.app.sales}>
                    <ContractorSales />
                  </Route>
                  <Route path={pageRoutes.app.billing}>
                    <Billing />
                  </Route>
                  <Route path={pageRoutes.app.myAccount}>
                    <MyAccount />
                  </Route>
                  <Route path={pageRoutes.app.faq}>
                    <FaqPage />
                  </Route>
                  <Route path={pageRoutes.app.reports}>
                    <ContractorReports>
                      <Switch>
                        <Route path={pageRoutes.app.reportsOverview}>
                          <OverviewReport />
                        </Route>
                        <Route path={pageRoutes.app.reportsPerformance}>
                          <PerformanceMetricsReport />
                        </Route>
                        <Route path={pageRoutes.app.reportsCampaignMap}>
                          <CampaignMap />
                        </Route>
                        <Route path={pageRoutes.app.reportsVerticalBenchmark}>
                          <VerticalBenchmark />
                        </Route>
                        <Redirect to={pageRoutes.app.reportsOverview} />
                      </Switch>
                    </ContractorReports>
                  </Route>
                  <Route path={pageRoutes.app.share}>
                    <Share />
                  </Route>
                  <Route
                    exact
                    path={pageRoutes.app.main}
                  >
                    <Redirect to={pageRoutes.app.campaigns} />
                  </Route>
                  <Route>
                    <Redirect to={pageRoutes.app.notFound} />
                  </Route>
                </Switch>
                <TermsAndConditionPopup />
              </ContractorLayoutContent>
            </Route>
          )}
          {role === Roles.Manager && (
            <Route>
              <Switch>
                <Route path={pageRoutes.internal.contractors}>
                  <ManagerContractors />
                </Route>
                <Route path={pageRoutes.internal.campaigns}>
                  <ManagerCampaigns />
                </Route>
                <Route path={pageRoutes.internal.leads}>
                  <ManagerLeads />
                </Route>
                <Route
                  path={pageRoutes.internal.groups}
                  exact
                >
                  <ManagerGroups />
                </Route>
                <Route
                  path={pageRoutes.internal.overlapping}
                  exact
                >
                  <ManagerOverlapping />
                </Route>
                <Route path={pageRoutes.internal.addNewCampaign}>
                  <ManagerAddCampaigns />
                </Route>
                <Route path={pageRoutes.internal.createNewGroup}>
                  <ManagerCreateNewGroup />
                </Route>
                <Route path={pageRoutes.internal.singleView}>
                  <ManagerGroupView />
                </Route>
                <Route path={pageRoutes.internal.appointments}>
                  <ManagerAppointments />
                </Route>
                <Route path={pageRoutes.internal.invoices}>
                  <Invoices />
                </Route>
                <Route path={pageRoutes.internal.sales}>
                  <ManagerSales />
                </Route>
                <Route path={pageRoutes.app.share}>
                  <Share />
                </Route>
                <Route
                  exact
                  path={pageRoutes.app.main}
                >
                  <Redirect to={pageRoutes.internal.contractors} />
                </Route>
                <Route>
                  <Redirect to={pageRoutes.app.notFound} />
                </Route>
              </Switch>
            </Route>
          )}
        </Switch>
      </Suspense>
    </AppLayout>
  );
}
